import * as React from "react";

import {
  Box,
  makeStyles,
  Theme,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import clsx from "clsx";

import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";

// TODO: move this to a png image hosted on static.afterpay.com
// https://apglobal.atlassian.net/browse/MERX-3855
const checkmarkCircle =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAADISURBVHgBpZINEcMgDIW5KUBCJFQCEiahTqiDxgE4qQQkIKESssDBLf0ZsO3dvd4B+XKPBqWuMuyVHdlUHNiODaohXUDqeC21FzgMwDLRocn6BSyTZMEPcLV58MeqQTnnVIxRbj3VyN211hRCoKRlWeRZ7taEAeATXP1ebNtG1toDzJFbcG4Q68J7n4v5rgd4nufWOBXKTUQkqQac7FIDcz5Icfd9p2maemOEOg7s/cwbo5zn30+5NhlJgnewFLD9KVEsoDkXvwAU//azLvsC2QAAAABJRU5ErkJggg==";

const useStyles = makeStyles((theme: Theme) => {
  const {
    stepCircleInProgressBg,
    stepCircleInProgressBorder,
    stepCircleNotStartedBg,
    stepCircleNotStartedBorder,
  } = useNormalizedBrandPaletteValues(theme);

  return {
    step: {
      display: "flex",
      position: "relative",
      zIndex: 2,
      [theme.breakpoints.down("md")]: {
        position: "initial",
        flexDirection: "column",
        flex: 1,
      },
    },
    stepLabelContainer: {
      left: 32,
      minWidth: "max-content",
      position: "absolute",
      top: -2,
      [theme.breakpoints.down("md")]: {
        position: "initial",
        marginTop: theme.spacing(0.5),
        top: 0,
        left: 0,
      },
    },
    stepLabel: {
      fontWeight: theme.typography.fontWeightBold,
    },
    stepCircle: {
      border: `1px solid ${theme.palette.common.black}`,
      height: "1rem",
      width: "1rem",
      borderRadius: "100%",
      backgroundSize: "cover",
      outline: theme.cash ? "4px solid white" : undefined,
    },
    stepCircleNotStarted: {
      backgroundColor: stepCircleNotStartedBg,
      borderColor: stepCircleNotStartedBorder,
    },
    stepCircleInProgress: {
      backgroundColor: stepCircleInProgressBg,
      borderColor: stepCircleInProgressBorder,
    },
    stepCircleComplete: {
      backgroundColor: theme.palette.common.black,
      borderColor: theme.palette.common.black,
      backgroundImage: theme.bronte ? `url(${checkmarkCircle})` : undefined,
    },
    animateComplete: {
      animation: "$growShrink .2s ease",
    },
    "@keyframes growShrink": {
      "0%": {
        transform: "scale(1)",
      },
      "50%": {
        transform: "scale(1.2)",
      },
      "100%": {
        transform: "scale(1)",
      },
    },
  };
});

interface StepIconProps {
  className: string;
  title: string;
}

export const StepIcon: React.FC<{ status: StepStatus }> = ({ status }) => {
  const {
    stepCircle,
    stepCircleNotStarted,
    stepCircleInProgress,
    stepCircleComplete,
    animateComplete,
  } = useStyles();

  function getStepIconClasses(): StepIconProps {
    const statusClasses: Record<StepStatus, StepIconProps> = {
      notStarted: {
        className: clsx(stepCircle, stepCircleNotStarted),
        title: "not started step",
      },
      inProgress: {
        className: clsx(stepCircle, stepCircleInProgress),
        title: "in progress step",
      },
      complete: {
        className: clsx(stepCircle, stepCircleComplete, animateComplete),
        title: "completed step",
      },
    };

    return statusClasses[status];
  }

  return <div {...getStepIconClasses()}></div>;
};

export type StepStatus = "complete" | "inProgress" | "notStarted";
export type StepName =
  | "Business Details"
  | "Payment Information"
  | "Customer Service";

interface IProps {
  status: StepStatus;
  label: string;
  stepKey: StepName;
}
export const Step: React.FC<IProps> = ({ status, label, stepKey }) => {
  const classes = useStyles();
  const isMobileWidth = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const getStepAlignment = () => {
    if (isMobileWidth) {
      if (stepKey === "Business Details") {
        return "start";
      }
      if (stepKey === "Payment Information") {
        return "center";
      }
      if (stepKey === "Customer Service") {
        return "flex-end";
      }
    }
    return "initial";
  };
  // on desktop always show label, on mobile only show label if step is in progress
  const isStepLabelVisible = !isMobileWidth || status === "inProgress";
  return (
    <Box
      data-testid={stepKey}
      className={classes.step}
      alignItems={getStepAlignment()}
    >
      <StepIcon status={status} />
      <Box
        component="span"
        className={classes.stepLabelContainer}
        textAlign={getStepAlignment()}
      >
        <Typography className={classes.stepLabel} variant="body2">
          {isStepLabelVisible ? label : ""}
        </Typography>
      </Box>
    </Box>
  );
};
